/**
 * RewardDialog — announces earned reward credits with terminal-aesthetic styling.
 * Shows one reward at a time from the pending queue.
 */

import { useTranslation } from 'react-i18next';
import type { PendingReward } from '@/api/rewardApi.ts';
import styles from './RewardDialog.module.css';

interface Props {
  reward: PendingReward;
  onDismiss: () => void;
}

interface RewardCopy {
  title: string;
  message: string;
  code: string;
}

function getRewardCopy(reward: PendingReward, t: (key: string, options?: Record<string, unknown>) => string): RewardCopy {
  switch (reward.event_type) {
    case 'social_share':
      return {
        title: t('rewardSocialShareTitle'),
        message: t('rewardSocialShareMessage'),
        code: `> reward_status: SUCCESS\n> balance_updated: +${reward.credits_awarded}_CREDITS`,
      };
    case 'reviewer_link':
      return {
        title: t('rewardReviewerLinkTitle'),
        message: t('rewardReviewerLinkMessage', { credits: reward.credits_awarded }),
        code: `> reward_status: SUCCESS\n> balance_updated: +${reward.credits_awarded}_CREDITS`,
      };
    case 'referral_given':
      return {
        title: t('rewardReferralGivenTitle'),
        message: t('rewardReferralGivenMessage', { credits: reward.credits_awarded }),
        code: `> referral_status: CONFIRMED\n> balance_updated: +${reward.credits_awarded}_CREDITS`,
      };
    case 'referral_received':
      return {
        title: t('rewardReferralReceivedTitle'),
        message: t('rewardReferralReceivedMessage', { credits: reward.credits_awarded }),
        code: `> referral_status: CLAIMED\n> bonus_credits: +${reward.credits_awarded}`,
      };
    default:
      return {
        title: t('rewardDefaultTitle'),
        message: t('rewardDefaultMessage', { credits: reward.credits_awarded }),
        code: `> reward: +${reward.credits_awarded}_CREDITS`,
      };
  }
}

export function RewardDialog({ reward, onDismiss }: Props) {
  const { t } = useTranslation('account');
  const copy = getRewardCopy(reward, t);

  return (
    <div className={styles.backdrop} onClick={onDismiss}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <div className={styles.title}>{copy.title}</div>
        <div className={styles.message}>{copy.message}</div>
        <div className={styles.codeBlock}>{copy.code}</div>
        <button className={styles.dismissBtn} onClick={onDismiss}>
          {t('continueButton')}
        </button>
      </div>
    </div>
  );
}
